import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { WeddingProduct, Variant } from "@/types/WeddingProduct";
import { SubCategory } from "@/types/Category";

export interface WeddingState {
  [key: string]: any;
  recordId: string;
  status: string;
  country: string;
  province: {
    id: string;
    name: string;
  };
  city: {
    id: string;
    name: string;
    region_name: string;
  };
  date: string;
  recipient: {
    firstName: string;
    lastName: string;
    address: string;
    addressType: string;
    unitNumber: string;
    buzzCode: string;
    city: string;
    province: string;
    postalCode: string;
    deliveryNote: string;
    isShippingAddressSame: string;
    shippingAddress: string;
    shippingCity: string;
    shippingPostalCode: string;
    shippingProvince: string;
    email: string;
    phoneNumber: string;
    referral: string;
  };
  stylesLocked: boolean;
  deliveryWindow: {
    title: string;
    price: string;
  };
  contactDetails: {
    name: string;
    phone: string;
  };
  addressUnsure: boolean;
  primaryBouquets: WeddingProduct[];
  secondaryBouquets: WeddingProduct[];
  decor: WeddingProduct[];
  wearables: WeddingProduct[];
  pressedBouquets: WeddingProduct[];
  extras: WeddingProduct[];
  focalFlowers: WeddingProduct[];
  featureFlowers: WeddingProduct[];
  standardFlowers: WeddingProduct[];
  greenery: WeddingProduct[];
  premiumDriedBotanicals: WeddingProduct[];
  resubmission: boolean;
  resubType: string;
  hydrationComplete: boolean;
}

const initialState: WeddingState = {
  recordId: "",
  status: "",
  country: "",
  province: {
    id: "",
    name: ""
  },
  city: {
    id: "",
    name: "",
    region_name: ""
  },
  date: "",
  recipient: {
    firstName: "",
    lastName: "",
    address: "",
    addressType: "",
    unitNumber: "",
    buzzCode: "",
    city: "",
    province: "",
    postalCode: "",
    deliveryNote: "",
    isShippingAddressSame: "",
    shippingAddress: "",
    shippingCity: "",
    shippingPostalCode: "",
    shippingProvince: "",
    email: "",
    phoneNumber: "",
    referral: ""
  },
  stylesLocked: false,
  deliveryWindow: {
    title: "",
    price: ""
  },
  contactDetails: {
    name: "",
    phone: ""
  },
  addressUnsure: false,
  primaryBouquets: [],
  secondaryBouquets: [],
  decor: [],
  wearables: [],
  pressedBouquets: [],
  extras: [],
  focalFlowers: [],
  featureFlowers: [],
  standardFlowers: [],
  greenery: [],
  premiumDriedBotanicals: [],
  resubmission: false,
  resubType: "",
  hydrationComplete: false
};

const normalizeRecipient = (recipient: (Partial<WeddingState["recipient"]> & { phone?: string }) | undefined) => {
  const data = recipient ?? {};
  return {
    ...initialState.recipient,
    ...data,
    phoneNumber: data.phoneNumber || data.phone || ""
  };
};

const normalizeDeliveryWindow = (deliveryWindow: Partial<WeddingState["deliveryWindow"]> | undefined) => ({
  ...initialState.deliveryWindow,
  ...deliveryWindow
});

const normalizeContactDetails = (contactDetails: Partial<WeddingState["contactDetails"]> | undefined) => ({
  ...initialState.contactDetails,
  ...contactDetails
});

const isDeepEqual = (a: unknown, b: unknown): boolean => {
  if (typeof a === "object" && typeof b === "object") {
    const keysA = Object.keys(a as Record<string, unknown>);
    const keysB = Object.keys(b as Record<string, unknown>);

    if (keysA.length !== keysB.length) {
      return false;
    }

    return keysA.every((key) => isDeepEqual((a as Record<string, unknown>)[key], (b as Record<string, unknown>)[key]));
  }

  return false;
};

const weddingSlice = createSlice({
  name: "wedding",
  initialState,
  reducers: {
    hydrateWeddingState: (state, action: PayloadAction<WeddingState>) => {
      if (!action.payload || isDeepEqual(action.payload, initialState)) {
        return state;
      }
      return {
        ...state,
        ...action.payload
      };
    },
    setResubmissionData: (state, action: PayloadAction<any>) => {
      const payload = action.payload ?? {};
      return {
        ...initialState,
        hydrationComplete: state.hydrationComplete,
        recordId: String(payload.recordId ?? ""),
        resubType: payload.resubType ?? "",
        status: payload.status ?? "",
        date: payload.date ?? "",
        recipient: normalizeRecipient(payload.recipient),
        extras: payload.extras ?? [],
        resubmission: true,
        stylesLocked: true
      };
    },
    setFullResubmissionData: (state, action: PayloadAction<any>) => {
      const payload = action.payload ?? {};
      return {
        ...initialState,
        hydrationComplete: state.hydrationComplete,
        recordId: String(payload.recordId ?? ""),
        resubType: payload.resubType ?? "",
        status: payload.status ?? "",
        date: payload.date ?? "",
        resubmission: true,
        stylesLocked: Boolean(payload.locked),
        recipient: normalizeRecipient(payload.recipient),
        addressUnsure: Boolean(payload.addressUnsure),
        deliveryWindow: normalizeDeliveryWindow(payload.deliveryWindow),
        contactDetails: normalizeContactDetails(payload.contactDetails),
        primaryBouquets: payload.primaryBouquets ?? [],
        secondaryBouquets: payload.secondaryBouquets ?? [],
        decor: payload.decor ?? [],
        wearables: payload.wearables ?? [],
        pressedBouquets: payload.pressedBouquets ?? [],
        extras: payload.extras ?? [],
        focalFlowers: payload.focalFlowers ?? [],
        featureFlowers: payload.featureFlowers ?? [],
        standardFlowers: payload.standardFlowers ?? [],
        greenery: payload.greenery ?? [],
        premiumDriedBotanicals: payload.premiumDriedBotanicals ?? []
      };
    },
    setDate: (state, action: PayloadAction<string>) => {
      state.date = action.payload;
    },
    setProducts: (state, action: PayloadAction<{ category: SubCategory; product: WeddingProduct }>) => {
      const { category, product } = action.payload;

      if (!Array.isArray(state[category])) {
        state[category] = [];
      }

      const currentList: WeddingProduct[] = state[category];
      const normalizedVariants = (product.variants ?? []).map((variant: Variant) => ({
        ...variant,
        quantity: Number(variant.quantity ?? 0)
      }));
      const hasQuantity = normalizedVariants.some((variant: Variant) => variant.quantity > 0);
      const normalizedProduct: WeddingProduct = {
        ...product,
        variants: normalizedVariants
      };

      const existingProductIndex = currentList.findIndex(
        (item: WeddingProduct) => item.title === normalizedProduct.title
      );

      if (existingProductIndex === -1) {
        if (hasQuantity) {
          currentList.push(normalizedProduct);
        }
        return;
      }

      if (!hasQuantity) {
        currentList.splice(existingProductIndex, 1);
        return;
      }

      const existingProduct = currentList[existingProductIndex];
      const mergedVariants = existingProduct.variants.map((existingVariant: Variant) => {
        const updatedVariant = normalizedVariants.find(
          (variant: Variant) => variant.title === existingVariant.title && variant.size === existingVariant.size
        );
        return updatedVariant ? { ...existingVariant, ...updatedVariant } : existingVariant;
      });

      normalizedVariants.forEach((variant: Variant) => {
        if (!mergedVariants.some((mergedVariant: Variant) =>
          mergedVariant.title === variant.title && mergedVariant.size === variant.size
        )) {
          mergedVariants.push(variant);
        }
      });

      currentList[existingProductIndex] = {
        ...existingProduct,
        ...normalizedProduct,
        variants: mergedVariants
      };
    },
    setRecipient: (state, action: PayloadAction<any>) => {
      state.recipient = action.payload;
    },
    setStylesLocked: (state, action: PayloadAction<boolean>) => {
      state.stylesLocked = action.payload;
    },
    setDeliveryWindow: (state, action: PayloadAction<{ title: string; price: string }>) => {
      state.deliveryWindow = action.payload;
    },
    setContactDetails: (state, action: PayloadAction<{ name: string; phone: string }>) => {
      state.contactDetails = action.payload;
    },
    setAddressUnsure: (state, action: PayloadAction<boolean>) => {
      state.addressUnsure = action.payload;
    },
    resetDateDependencies: (state) => {
      state.primaryBouquets = [];
      state.secondaryBouquets = [];
      state.decor = [];
      state.wearables = [];
      state.pressedBouquets = [];
      state.extras = [];
      state.focalFlowers = [];
      state.featureFlowers = [];
      state.standardFlowers = [];
      state.greenery = [];
      state.premiumDriedBotanicals = [];
      state.deliveryWindow = { ...initialState.deliveryWindow };
    },
    flushWeddingItem: (state) => {
      return {
        ...initialState,
        hydrationComplete: true
      };
    },
    setHydrationComplete: (state, action: PayloadAction<boolean>) => {
      state.hydrationComplete = action.payload;
    }
  }
});

export const {
  hydrateWeddingState,
  setResubmissionData,
  setFullResubmissionData,
  setDate,
  setProducts,
  setRecipient,
  setStylesLocked,
  setDeliveryWindow,
  setContactDetails,
  setAddressUnsure,
  resetDateDependencies,
  flushWeddingItem,
  setHydrationComplete
} = weddingSlice.actions;

export default weddingSlice.reducer;
